import React, { useState, useEffect, useRef } from 'react';
import { downloadImage, findOne, getVideoFrame, loadBase64Image, stopProp } from '../../utils';
import { Dialog, DialogActions, DialogContent, Select, Icon, MenuItem, ToggleButton, ToggleButtonGroup, TextField, Tooltip, IconButton as MuiIconButton } from '@mui/material';
import { get, size, set, noop } from 'global/lib/lodash';
import useStyles, { COLORS } from './styles';
import { Button, IconButton } from '../Button';
import clsx from 'clsx';
import ImageEditor, { TABS, TOOLS } from './FilerobotImageEditor';
import { Text } from '../text';
import { Column, Row } from '../row';
import MImage from '../image/Image';
import { Checkbox } from '../checkbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getRealmIcon } from '../droprealms';
import moment from 'moment';
import { CenterLoader, Loader, useScreen } from '../../styles';
import { useEditData, isHighlight, createKeyBindings } from './utils';
import ReactPlayer from 'react-player';
import CheckBoxOutlineBlankOutlined from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import useImageGridStyles from '../image-grid/styles';

export const getSource = (props, arr = ['thumb', 'thumb-url', 'thumbUrl', 'large', 'large-url', 'largeUrl', 'fullUrl', 'data']) => {
  return findOne(props, arr);
};

export const isLocal = (props) => {
  const src = `${getSource(props)}`;
  // `blob:` URLs are produced by URL.createObjectURL for in-memory previews of
  // files that have not yet been uploaded. Treat them as local, same as base64
  // `data:` URIs, so the Media component renders the preview rather than
  // attempting to fetch backing server data that does not exist yet.
  return !src || src.startsWith('data:') || src.startsWith('blob:');
};

export const VideoTypes = ['.mp4', '.mov', '.avi', '.m4v'];
export const checkIfVideo = (src) => {
  return !!VideoTypes.find((s) => (src || '').toLowerCase().includes(s));
};

export const Media = (props) => {
  const [edit, setEdit] = useState({
    open: false,
    onOpen: () => {
      setEdit((e) => ({ ...e, open: true }));
      props.onOpen && props.onOpen();
    },
    onClose: (s) => {
      setEdit((e) => ({ ...e, open: false }));
      props.onClose && props.onClose(s);
    },
  });

  const [state, setState] = useState({
    tempVideoFrame: null,
  });

  const canEdit = get(props, 'canEdit', true);
  const showCaption = get(props, 'showCaption', false);
  const loading = get(props, 'loading', false);
  const canSelect = get(props, 'canSelect', false);
  const imageSel = get(props, 'imageSel', false);

  const classes = useStyles();
  const gridClasses = useImageGridStyles();

  const onClick = (e) => {
    if (canEdit && !loading) {
      stopProp(e);
      edit.onOpen();
    }
  };

  const selectImage = (e) => {
    stopProp(e);
    props.selectImage && props.selectImage(props);
  };

  const renderCaption = () => {
    const cap =
      get(props, 'caption') ||
      get(props, 'custom_properties.caption') ||
      get(props, 'customProperties.caption');

    if (!showCaption || !cap) {
      return null;
    }

    if (typeof cap === 'function') {
      return cap();
    }

    return (
      <div style={props.captionStyle} className={`caption`}>
        <Text type={'label_tiny'}>{cap}</Text>
      </div>
    );
  };

  const src = getSource(props, props.sourceType) || getSource(get(props, `images.${props.index}`)) || '';
  const isVideo = checkIfVideo(src);

  const loadTempVideoFrame = async () => {
    try {
      const vframe = await getVideoFrame(src);
      setState((s) => ({ ...s, tempVideoFrame: vframe }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (isVideo) {
      loadTempVideoFrame();
    }
  }, [isVideo]);

  const renderMediaType = () => {
    const fullIsVideo = checkIfVideo(getSource(props, ['fullUrl', 'full']));

    const _src = state.tempVideoFrame || src;

    return (
      <div className={'smallMediaContainer'} onClick={onClick}>
        <MImage src={_src} />
        {fullIsVideo && <Icon className={'playIcon'}>play_circle</Icon>}
        {loading && Loader({ className: 'playIcon', size: 20 })}
      </div>
    );
  };

  const renderMedia = () => {
    return (
      <div className={clsx(classes.mediaDisplay, canSelect && 'canSelect', imageSel && 'selected', props.printView && 'printView')} style={props.outerContainerStyle}>
        <div style={{ ...(canSelect ? { width: '100%', height: '100%' } : {}), ...props.style }}>{renderMediaType()}</div>
        {props.overlay && props.overlay()}
        {renderCaption()}
        {canSelect && (
          <MuiIconButton
            onClick={selectImage}
            classes={{ root: gridClasses.iconRoot }}
            className={clsx(gridClasses.checkIcon, imageSel && gridClasses.checkIconSelected, props.selectMode && gridClasses.checkIconSelectMode)}
            size="large"
          >
            {imageSel ? <CheckBoxIcon /> : <CheckBoxOutlineBlankOutlined />}
          </MuiIconButton>
        )}
        {!!edit.open && <FullScreenMedia {...props} {...edit} />}
      </div>
    );
  };

  return renderMedia();
};

const VideoWrap = (p) => {
  return <div {...p} style={{ width: '100%', height: '100%' }} />;
};

export const FullScreenMedia = (props = {}) => {
  const viewOnlyAllowed = get(props, 'viewOnlyAllowed', ['zoom_in', 'zoom_out', 'download']);
  const allowed = get(props, 'allowed', props.viewOnly ? viewOnlyAllowed : ['zoom_in', 'zoom_out', 'caption', 'tag', 'edit', 'download', 'more']);
  const notAllowed = get(props, 'notAllowed', []);
  const notAllowedLocal = get(props, 'notAllowedLocal', ['edit', 'more']);
  const showLink = get(props, 'showLink', false);
  const screen = useScreen();
  const editorStateRef = useRef({});
  // Filerobot fires onModify when the source is replaced, including after a save
  // and when the arrows load the next photo. Only a pointer in the editor counts.
  const allowEditorModifyRef = useRef(false);

  const [state, setState] = useState({
    index: props.index || 0,
    // The opened file is on `props`, so its mediaId would be copied onto every
    // slide. Sub-score photos already have their own mediaId and overwrite it.
    // Score photos only have `id`, and that id is the same media identifier.
    // Keep each slide's own id. Leaving mediaId empty makes the editor use the
    // parent score's first image and save the annotation onto that file.
    images: (props.images || [props]).map((img) => ({ ...props, ...img, mediaId: img.mediaId || img.id })),
    hasEditorEdits: false,
    textFocus: false,
    zoom: 1,
    zoomPos: { x: 0, y: 0 },
    start: { x: 0, y: 0 },
    loading: false,
    opt: (() => {
      let o = props.opt !== undefined ? props.opt : undefined;

      if (!allowed.includes(o)) {
        o = null;
      }

      return o;
    })(),
    tabs: [
      { key: 'zoom_out', icon: 'zoom_out', onClick: 'zoom', show: (p) => !p.isVideo && p.opt != 'edit' },
      { key: 'zoom_in', icon: 'zoom_in', onClick: 'zoom', show: (p) => !p.isVideo && p.opt != 'edit' },
      { key: 'tag', icon: 'label', show: (p) => p.hasValidData },
      { key: 'edit', icon: 'edit', show: (p) => p.hasValidData && !p.isVideo },
      { key: 'download', icon: 'download', onClick: 'downloadTheImage' },
      { key: 'more', icon: 'more_vert', show: (p) => !p.isVideo },
    ],
    imageSource: null,
    showMore: false,
  });

  const IMG = state.images[state.index];

  const adata = useEditData(IMG);
  const {
    hasEdits,
    realm,
    collectionName,
    supersets,
    assessment,
    values,
    setValues: svalues,
    imgdata,
    mediadata,
    originalId,
    loading: dloading,
    hasValidData,
    updateImage,
    getOriginalImage,
    setOriginalImage,
    deleteImage,
  } = adata;

  const imageRep = mediadata.id && !isLocal(IMG) ? mediadata : IMG;
  const src = getSource(imageRep, ['large', 'largeUrl', 'large-url', 'full', 'fullUrl', 'data']);
  const isVideo = checkIfVideo(src);

  const loading = dloading || state.loading;

  const downloadTheImage = () => {
    downloadImage({ ...IMG, large: getSource(imageRep, ['full', 'fullUrl', 'large', 'largeUrl', 'large-url', 'data']) });
  };

  const zoom = (tab) => {
    const amt = tab.key.includes('_out') ? -0.25 : 0.25;

    setState((s) => {
      // Clamp to the same bounds the scroll-zoom handler uses (min 1, max 9).
      const nextZoom = Math.min(9, Math.max(1, s.zoom + amt));

      // No effective change (already at a bound) — leave pan untouched.
      if (nextZoom === s.zoom) {
        return s;
      }

      // Zoom about the CENTER of the current viewport, keeping the content point
      // under that center fixed. onScroll anchors on e.clientX/e.clientY, but the
      // transform's screen position is `screen = O + zoomPos + zoom * L`, where O
      // is the transformed element's UNTRANSFORMED top-left in viewport space and
      // L is the content point measured from that origin (transformOrigin is the
      // top-left of the MImage wrapper). The anchor must therefore be expressed
      // RELATIVE TO THE TRANSFORM ORIGIN, not in absolute viewport coordinates —
      // using the absolute anchor leaves a residual error of O·(zoom-1) and the
      // image drifts. The MImage wrapper fills .imagecontainer's content box, so
      // its center IS the viewport center; relative to its own top-left, that
      // center is simply half its untransformed size. offsetWidth/offsetHeight
      // give the LAYOUT (untransformed) size and ignore the CSS transform, so they
      // stay correct at any zoom.
      const container = document.querySelector('.imagecontainer');
      const wrap = container && container.firstElementChild; // the MImage wrapper div carrying the transform
      const W = wrap ? wrap.offsetWidth : container ? container.clientWidth : 0;
      const H = wrap ? wrap.offsetHeight : container ? container.clientHeight : 0;

      // Fallback: if we can't measure, just change zoom without re-anchoring.
      if (!W || !H) {
        return { ...s, zoom: nextZoom };
      }

      // Viewport center expressed relative to the transform origin.
      const bx = W / 2;
      const by = H / 2;

      // Content point currently under the viewport center (origin-relative),
      // then recompute the translate so that same point stays under the center.
      const xs = (bx - s.zoomPos.x) / s.zoom;
      const ys = (by - s.zoomPos.y) / s.zoom;
      const x = bx - xs * nextZoom;
      const y = by - ys * nextZoom;

      // At zoom 1 settle the pan back to (0,0) so the image re-centers, matching
      // onScroll's behavior at the lower bound.
      const zoomPos = nextZoom === 1 ? { x: 0, y: 0 } : { x, y };

      return {
        ...s,
        zoom: nextZoom,
        zoomPos,
        // Keep the drag baseline consistent with the new pan so the next
        // mouse-move doesn't jump. on_mousedown rederives `start` from zoomPos,
        // but updating it here guards against any stale read in between.
        start: s.panning ? { x: s.start.x + (zoomPos.x - s.zoomPos.x), y: s.start.y + (zoomPos.y - s.zoomPos.y) } : s.start,
      };
    });
  };

  const setValues = (f) => {
    setState((s) => {
      let img = get(s, `images.${s.index}`);
      set(s, `images.${s.index}`, f({ ...img, ...values }));
      return { ...s };
    });
    svalues(f);
  };

  const capEditAllowed = allowed.includes('caption');
  const canCap = capEditAllowed && hasValidData;
  const capVal = get(values, 'custom_properties.caption', '');
  const hideCap = screen.isMobile && !state.showMore && !canCap && !capVal;

  const classes = useStyles({ ...state, ...screen, showLink, values, canCap, capVal, hideCap });

  const handleClose = (hEdits = hasEdits) => {
    if (canContinue()) {
      props.onClose && props.onClose({ ...state, hasEdits: hEdits });
    }
  };

  const isAllowed = (check) => {
    if (notAllowed.includes(check)) {
      return false;
    }

    if (isLocal(IMG) && notAllowedLocal.includes(check)) {
      return false;
    }

    return allowed.includes(check);
  };

  const tabButton = (t) => {
    if (!isAllowed(t.key)) {
      return null;
    }

    if (t.show && !t.show({ ...state, hasValidData, isVideo })) {
      return null;
    }

    const isSelected = state.opt == t.key;

    const METHODS = {
      downloadTheImage,
      zoom,
    };

    const tabProps = { ...t, ...get(props, `tabProps.${t.key}`, {}) };

    const onClick = (t) => {
      if (tabProps.disabled) {
        return;
      }

      if (canContinue()) {
        t.onClick ? get(METHODS, t.onClick, () => 0)(t) : setState((s) => ({ ...s, opt: s.opt == t.key ? null : t.key }));
      }
    };

    const Comp = (
      <div {...tabProps} className={clsx('tab', t.key, isSelected && 'selected')} onClick={() => onClick(t)}>
        <Icon>{t.icon}</Icon>
      </div>
    );

    if (tabProps.toolTip) {
      return (
        <Tooltip title={tabProps.toolTip}>
          <span>{Comp}</span>
        </Tooltip>
      );
    }

    return Comp;
  };

  const renderHeader = () => {
    return (
      <div className="MuiDialogHeader-root">
        <IconButton variant="text" icon="arrow_back" onClick={() => handleClose()} />

        <div className="toolbar">{state.tabs.map(tabButton)}</div>
      </div>
    );
  };

  const setSuperset = (v) => {
    setValues((vv) => ({ ...vv, supersets: vv.supersets.includes(v) ? vv.supersets.filter((f) => f != v) : [...vv.supersets, v] }));
  };

  const canContinue = () => {
    let cont = true;
    if (state.hasEditorEdits) {
      cont = window.confirm('You have not saved your edits by hitting the save button. Are you sure you wish to continue?');
    }
    return cont;
  };

  const changeDir = (d, force = false) => {
    if (canContinue()) {
      setState((s) => {
        if (s.textFocus && !force) {
          return s;
        }

        let index = s.index + d;

        if (index > s.images.length - 1) {
          index = 0;
        } else if (index < 0) {
          index = s.images.length - 1;
        }

        allowEditorModifyRef.current = false;
        return {
          ...s,
          index,
          zoom: 1,
          hasEditorEdits: false,
          panning: false,
          zoomPos: { x: 0, y: 0 },
          start: { x: 0, y: 0 },
        };
      });
    }
  };

  const dirButtons = () => {
    const hasImages = size(props.images) > 1;
    if (!hasImages) {
      return null;
    }

    if (screen.isMobile && state.opt && state.opt != 'edit') {
      return null;
    }

    return (
      <div className="dirButtons">
        <button className="dir prev" onClick={() => changeDir(-1, true)}>
          <Icon>arrow_back_ios</Icon>
        </button>
        <button className="dir next" onClick={() => changeDir(1, true)}>
          <Icon>arrow_forward_ios</Icon>
        </button>
      </div>
    );
  };

  const isElementInput = (e) => {
    const elems = ['INPUT', 'TEXTAREA'];

    const iselem = elems.includes(get(e, 'tagName'));
    return iselem && get(e, 'checked') === undefined;
  };

  const loadEditImage = (keys = ['full', 'fullUrl']) => {
    const checkImageDims = (s) => {
      return new Promise((res, rej) => {
        const img = new Image();
        img.src = s;

        //images that are too big screw up canvas to edit image
        const toobig = { width: 3000, height: 3000 };

        img.onload = () => {
          if (img.width > toobig.width || img.height > toobig.height) {
            return rej(new Error('too big'));
          }
          res(s);
        };

        img.onerror = (e) => rej(e);
      });
    };

    return new Promise(async (res, rej) => {
      const sc = await checkImageDims(getSource(imageRep, keys)).catch(() => getSource(imageRep, ['large', 'largeUrl', 'large-url', 'full', 'fullUrl']));

      const i = await loadBase64Image(sc);
      res(i);
    });
  };

  useEffect(() => {
    //we are doing this becuase of this issue: https://github.com/scaleflex/filerobot-image-editor/issues/319
    const loadImage = async () => {
      try {
        setState((s) => ({ ...s, loading: true }));

        const image = await loadEditImage();

        allowEditorModifyRef.current = false;
        setState((s) => ({ ...s, imageSource: image, hasEditorEdits: false }));
      } catch (err) {
        alert(err.toString());
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    };

    if (state.opt === 'edit' && src && !isVideo) {
      loadImage();
    }
  }, [state.opt, src]);

  useEffect(() => {
    let keys = () => 0;

    setTimeout(() => {
      const elem = document.querySelector('.imagecontainer');

      const hasImages = size(props.images) > 1;

      const ignorecmds = isVideo || state.opt == 'edit';

      keys = createKeyBindings({
        element: elem,
        onEscape: () => handleClose(),
        onArrowRight: (e) => {
          if (isElementInput(e.srcElement)) {
            return;
          }
          return hasImages ? changeDir(1) : noop();
        },
        onArrowLeft: (e) => {
          if (isElementInput(e.srcElement)) {
            return;
          }
          return hasImages ? changeDir(-1) : noop();
        },
        on_mousemove: ignorecmds
          ? noop
          : (e) => {
              e.preventDefault();
              setState((s) => {
                if (!s.panning) {
                  return s;
                }

                return {
                  ...s,
                  zoomPos: { x: e.clientX - s.start.x, y: e.clientY - s.start.y },
                };
              });
            },
        on_mousedown: ignorecmds
          ? noop
          : (e) => {
              e.preventDefault();
              setState((s) => ({ ...s, panning: true, start: { x: e.clientX - s.zoomPos.x, y: e.clientY - s.zoomPos.y } }));
            },
        on_mouseleave: ignorecmds
          ? noop
          : (e) => {
              setState((s) => ({ ...s, panning: false }));
            },
        on_mouseup: ignorecmds
          ? noop
          : (e) => {
              e.preventDefault();
              setState((s) => ({ ...s, panning: false }));
            },
        onScroll: ignorecmds
          ? noop
          : (e) => {
              setState((s) => {
                try {
                  const xs = (e.clientX - s.zoomPos.x) / s.zoom;
                  const ys = (e.clientY - s.zoomPos.y) / s.zoom;
                  const delta = e.wheelDelta ? e.wheelDelta : -e.deltaY;
                  const scale = delta > 0 ? s.zoom * 1.05 : s.zoom / 1.05;
                  const x = e.clientX - xs * scale;
                  const y = e.clientY - ys * scale;

                  return {
                    ...s,
                    zoom: scale < 1 ? 1 : scale > 9 ? 9 : scale,
                    zoomPos: scale < 1 ? { x: 0, y: 0 } : scale > 9 ? s.zoomPos : { x, y },
                  };
                } catch (err) {
                  console.error(err);
                }
              });
            },
      });
    }, 250);

    return () => {
      keys();
    };
  }, [state.opt, src, state.hasEditorEdits, isVideo]);

  useEffect(() => {
    if (screen.isMobile && state.opt) {
      setState((s) => ({ ...s, opt: null }));
    } else {
      setState((s) => ({ ...s, showMore: false }));
    }
  }, [screen.isMobile]);

  const renderMediaType = () => {
    if (isVideo) {
      return (
        <Column center style={{ height: '100%' }}>
          <ReactPlayer key={src} wrapper={VideoWrap} url={src} controls={true} />
        </Column>
      );
    }

    // The zoom transform must land on the wrapper <div>. The custom Image
    // component (which replaced mui-image in the MUI upgrade) applies `style` to
    // its wrapper — mui-image's old `wrapperStyle` prop is no longer read, which
    // is why the zoom in/out buttons (and scroll-zoom) silently did nothing. See PFM-2705.
    return <MImage style={{ transform: `scale(${state.zoom})`, translate: `${state.zoomPos.x}px ${state.zoomPos.y}px`, transformOrigin: '0px 0px' }} src={src} />;
  };

  const renderImage = () => {
    const noTab = !state.opt;
    return (
      <div className={`imagecontainer ${noTab ? 'noTab' : ''}`}>
        {renderMediaType()}
        {dirButtons()}
      </div>
    );
  };

  const renderSelectedTab = () => {
    if (state.opt === 'tag') {
      return (
        <Row fullHeight>
          {renderImage()}

          <div className="sidebar right">
            <Text bold center>
              TAG
            </Text>

            <div className="spaceVert">
              <Text bold>Quality</Text>

              <ToggleButtonGroup exclusive value={values.quality} onChange={(e, q) => setValues((v) => ({ ...v, quality: q }))}>
                <ToggleButton value={null}>
                  <Icon>close</Icon>
                </ToggleButton>
                <ToggleButton value={1}>
                  <Icon>thumb_up</Icon>
                </ToggleButton>
                <ToggleButton value={0}>
                  <Icon>thumb_down</Icon>
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            <div className="spaceVert">
              <Text bold>Location</Text>

              <Select size="small" fullWidth placeholder={'Select a Location'} value={values.location} onChange={(e) => setValues((v) => ({ ...v, location: e.target.value }))}>
                {get(assessment, 'locations', []).map((l) => (
                  <MenuItem value={l.id}>{l.name}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="spaceVert">
              <Text bold>Category</Text>
              <div>
                {supersets.map((s) => (
                  <Checkbox checkProps={{ size: 'small' }} checked={values.supersets.includes(s.id)} onClick={() => setSuperset(s.id)} label={s.name} />
                ))}
              </div>
            </div>
          </div>
        </Row>
      );
    } else if (state.opt == 'edit') {
      return renderImageEditor();
    } else if (state.opt == 'more') {
      const hasOriginal = originalId;

      const opts = [
        hasOriginal && { title: 'Download Original', icon: 'download', onClick: () => getOriginalImage().then((r) => downloadImage({ ...r, large: r.fullUrl })) },
        hasOriginal && { title: 'Reset to Original', icon: 'restore', onClick: setOriginalImage },
        { title: 'Delete', icon: 'delete', onClick: () => deleteImage().then(() => handleClose(true)) },
      ].filter((f) => !!f);

      return (
        <Row fullHeight>
          {renderImage()}

          <div className="sidebar right more">
            <Text bold center>
              MORE
            </Text>

            {opts.map((o) => (
              <Row>
                <Button variant="text" color="secondary" {...o} />
              </Row>
            ))}
          </div>
        </Row>
      );
    }

    return <Row fullHeight>{renderImage()}</Row>;
  };

  const onImageEditedSave = async (imgObj, designState) => {
    allowEditorModifyRef.current = false;
    try {
      await updateImage(imgObj);
      setState((s) => ({ ...s, hasEdits: true, hasEditorEdits: false }));
    } catch (err) {
      console.error(err);
    }
  };

  const renderImageEditor = () => {
    if (!state.imageSource) {
      return null;
    }

    const ShapeProps = {
      fill: '#00000000',
      stroke: '#ff0000',
      strokeWidth: 4,
    };

    return (
      <div style={{ position: 'absolute', inset: 0 }} onPointerDownCapture={() => { allowEditorModifyRef.current = true; }}>
        <ImageEditor
          source={state.imageSource}
          onBeforeSave={() => false}
          onSave={onImageEditedSave}
          resetOnImageSourceChange
          // noCrossOrigin
          annotationsCommon={{
            fill: '#ff0000',
            stroke: '#ff0000',
          }}
          theme={{
            palette: {
              'bg-secondary': COLORS.bgSecondary,
              'bg-primary': COLORS.bgPrimary,
              'bg-primary-active': COLORS.bgPrimaryActive,
              'accent-primary': COLORS.accentPrimary,
              'accent-primary-active': COLORS.accentPrimaryActive,
              'icons-primary': COLORS.iconsPrimary,
              'icons-secondary': COLORS.iconsSecondary,
              'borders-secondary': '#464646',
              'borders-primary': 'blue',
              'borders-strong': 'magenta',
              'light-shadow': 'rgba(0,0,0,0.8)',
              warning: 'orange',
            },
            typography: {
              color: 'white',
            },
          }}
          Pen={{
            strokeWidth: 4,
          }}
          Line={{
            strokeWidth: 4,
          }}
          Text={{
            text: `Double-click to edit, press “Enter” to save`,
            fonts: ['Arial', 'Tahoma', 'Sans-serif'],
          }}
          Ellipse={ShapeProps}
          Rect={ShapeProps}
          Polygon={ShapeProps}
          onModify={() => {
            if (!allowEditorModifyRef.current) return;
            setState((s) => ({ ...s, hasEditorEdits: true }));
          }}
          updateStateFnRef={editorStateRef}
          Rotate={{ angle: 90, componentType: 'slider' }}
          tabsIds={[TABS.ANNOTATE, TABS.ADJUST, TABS.FINETUNE]}
          defaultTabId={TABS.ANNOTATE}
          defaultToolId={TOOLS.PEN}
        />
        {dirButtons()}
      </div>
    );
  };

  const renderContent = () => {
    return <DialogContent>{renderSelectedTab()}</DialogContent>;
  };

  const selectLink = () => {
    let link = '';
    if (collectionName === 'scorecard-score-images') {
      link = `/reports/assessment/${get(imgdata, 'assessmentId')}/scores`;
    } else if (collectionName === 'task-images' || collectionName === 'comment-images') {
      link = `/tasks/detail/${get(props, 'task.id')}`;
    } else if (isHighlight(collectionName)) {
      link = `/reports/assessment/${get(assessment, 'id')}/highlights`;
    } else if (collectionName === 'realm-images') {
      link = `/reports/assessment/${get(assessment, 'id')}/photos`;
    }

    if (!link) {
      return alert('TODO');
    }

    window.open(link, '_blank');
  };

  const toggleMore = () => {
    setState((s) => ({ ...s, showMore: !s.showMore }));
  };

  const renderFooter = () => {
    const hasRender = props.renderFooter;

    if (screen.isMobile && !!state.opt) {
      return null;
    }

    if (state.opt == 'edit') {
      return null;
    }

    const createdAt = get(mediadata, 'createdAt', get(IMG, 'createdAt'));
    const author = get(props, 'showAuthor', true)
      ? get(IMG, 'authors.0', get(IMG, 'task.authors.0', get(imgdata, 'authors.0', get(adata, '0.assessment.authors.0', get(adata, 'assessment.authors.0')))))
      : null;
    const description = get(IMG, 'task.description', get(IMG, 'description', get(props, 'description', get(props, 'task.description'))));

    return (
      <DialogActions>
        <Row fullWidth className="footerContainer">
          {hasRender ? (
            props.renderFooter({ ...props, values })
          ) : (
            <>
              <Row className="info">
                {props.icon || getRealmIcon(realm)}
                <div className="infotext">
                  <Text bold>{get(mediadata, 'name', get(props, 'name', ''))}</Text>
                  {!!realm && (
                    <Text type="label_medium">{`${get(realm, 'name', '')}, ${author ? `${get(author, 'name', '')}, ` : ''} ${createdAt ? moment(createdAt).format('L') : ''}`}</Text>
                  )}
                </div>
              </Row>

              <div className="caption">
                {!hideCap && (
                  <TextField
                    disabled={!canCap}
                    fullWidth
                    multiline
                    minRows={screen.isMobile && !state.showMore ? 1 : !!description ? 1 : 2}
                    variant="outlined"
                    size="small"
                    onFocus={() => setState((s) => ({ ...s, textFocus: true }))}
                    onBlur={() => setState((s) => ({ ...s, textFocus: false }))}
                    placeholder={canCap ? 'enter caption here' : ''}
                    value={loading ? '' : get(values, 'custom_properties.caption', '')}
                    onChange={(e) => setValues((v) => ({ ...v, custom_properties: { ...v.custom_properties, caption: e.target.value } }))}
                    style={{ height: !!description ? 40 : 'auto' }}
                  />
                )}

                {!!description && (
                  <div className="descriptionText">
                    <span dangerouslySetInnerHTML={{ __html: description }} />
                  </div>
                )}

                {screen.isMobile && !hideCap && (
                  <div onClick={toggleMore} className="more link">
                    {state.showMore ? 'Less' : 'More'}
                  </div>
                )}
              </div>

              <Row className="info right" center>
                {showLink && !(isHighlight(collectionName) && !get(assessment, 'id') && !get(props, 'assessment.id')) && (
                  <div className="link" onClick={selectLink}>
                    Open Reference
                  </div>
                )}
              </Row>
            </>
          )}
        </Row>
      </DialogActions>
    );
  };

  const renderLoading = () => {
    if (loading) {
      return (
        <div className="loadingwindow">
          <CenterLoader />
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog disableEscapeKeyDown open={!!props.open} fullScreen fullWidth classes={{ container: classes.editMediaContainer }}>
      {renderHeader()}

      {renderContent()}

      {renderFooter()}

      {renderLoading()}
    </Dialog>
  );
};
